import { Autosuggest, AutosuggestProps, FormField, Link } from '@amzn/awsui-components-react';
import { useState } from 'react';
import { useLocalizedText } from '../../../../hooks/localizedText';
import { loadCauseSuggestions } from '../../../../hooks/causes';
import { OptionalLabel } from './Common';

import { CommunityOrganization, EventInput } from '@amzn/red-velvet-api';
import { useSearchParams } from 'react-router-dom';
import { getEnabledFeatures, useFeatures } from '../../../../hooks/features';
import { publishKatalMetric } from '../../../katalAnalytics';

// Constants for input validation
const MIN_CHARACTERS_FOR_AUTOCOMPLETE_INPUT = 2;
const MAX_CHARACTERS_FOR_AUTOCOMPLETE_INPUT = 180;

export interface CommunityOrganizationComponentProps {
  currentFields: EventInput;
  updateFieldHandler: (fields: EventInput) => void;
  isReadOnly?: boolean;
  metricPrefix: string;
  onCommunityInfoClick?: () => void;
}

export function CommunityOrganizationComponent(props: CommunityOrganizationComponentProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useLocalizedText(namespaces);
  const [searchParams] = useSearchParams();
  const features = useFeatures();

  const communityOrg = props.currentFields.communityOrganizations?.[0];
  const [value, setValue] = useState(communityOrg?.name ?? '');
  const [options, setOptions] = useState<AutosuggestProps.Options>([]);
  const [status, setStatus] = useState<AutosuggestProps.StatusType>();

  const loadCauseOptions = async (filteringText: string) => {
    //Cause autocomplete api only loads with 3+ characters and limit of 180 characters
    if (
      filteringText.length > MIN_CHARACTERS_FOR_AUTOCOMPLETE_INPUT &&
      filteringText.length <= MAX_CHARACTERS_FOR_AUTOCOMPLETE_INPUT
    ) {
      try {
        setStatus('loading');
        setOptions([]);
        const causeOptions = await loadCauseSuggestions(
          filteringText,
          getEnabledFeatures(features, searchParams)
        );
        setOptions(causeOptions);
        setStatus('finished');
      } catch (e) {
        setStatus('error');
      }
    } else {
      setOptions([]);
    }
  };

  return (
    <FormField
      info={
        props.onCommunityInfoClick && (
          <Link
            data-testid="comm-info-help-button"
            variant="info"
            data-aci-analytics-name="eventCreation_communityOrgInfoOpened"
            onFollow={(e) => {
              e.preventDefault();
              if (props.onCommunityInfoClick) {
                props.onCommunityInfoClick();
              }
            }}
          >
            {t('misc.info')}
          </Link>
        )
      }
      label={<OptionalLabel>{t('eventDetailsStep.communityOrganization')}</OptionalLabel>}
      description={t('eventDetailsStep.communityOrganizationSubText')}
    >
      <Autosuggest
        filteringType="manual"
        value={value}
        onChange={({ detail }) => {
          const isSelection = options.some((o) => 'value' in o && o.value === detail.value);
          if (isSelection) return; // let onSelect handle value and parent update
          setValue(detail.value);
          const currentOrg = props.currentFields.communityOrganizations?.[0];
          if (!detail.value || (currentOrg && detail.value !== currentOrg.name)) {
            props.updateFieldHandler({
              ...props.currentFields,
              communityOrganizations: [],
            });
          }
        }}
        onSelect={({ detail }) => {
          const option = options.find((o) => 'value' in o && o.value === detail.value);
          if (option && 'label' in option) {
            setValue(option.label ?? detail.value);
            publishKatalMetric(
              `${props.metricPrefix}_communityOrgSelected`,
              option.label ?? detail.value
            );
            props.updateFieldHandler({
              ...props.currentFields,
              communityOrganizations: [
                {
                  name: option.label ?? detail.value,
                  causeId: detail.value,
                } as CommunityOrganization,
              ],
            });
          }
        }}
        onLoadItems={({ detail }) => {
          loadCauseOptions(detail.filteringText);
        }}
        options={options}
        statusType={status}
        placeholder={t('eventDetailsStep.communityOrganizationPlaceholder')}
        empty={t('additionalInfo.noCauseSuggestions')}
        loadingText={t('additionalInfo.loadingCauseSuggestions')}
        readOnly={props.isReadOnly}
      />
    </FormField>
  );
}
